import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { toast } from "sonner";
import {
  Activity,
  Check,
  ClipboardList,
  CreditCard,
  Download,
  Eye,
  FileDown,
  IndianRupee,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquare,
  Package,
  RefreshCw,
  Search,
  Settings2,
  Shield,
  ShoppingBag,
  Star,
  TicketPercent,
  Trash2,
  Truck,
  Users,
  X,
} from "lucide-react";
import { db, functions, storage } from "@/lib/firebase";
import { useAuth } from "@/context/AuthContext";
import { formatINR, LIFE_OS_VARIANT_PRICING, PRODUCTS } from "@/lib/products";

const SUPER_ADMIN_EMAIL = "kairox.1302@gmail.com";
const ADMIN_EMAIL = "pixelpy18@gmail.com";

const ORDER_STATUSES = [
  "pending",
  "confirmed",
  "accepted",
  "packed",
  "shipped",
  "out_for_delivery",
  "delivered",
  "cancelled",
  "refunded",
];

const CONTROL_DEFAULTS = {
  registrationEnabled: true,
  paymentsEnabled: true,
  checkoutEnabled: true,
  couponCheckoutEnabled: true,
};

const RECOVERY_FORM_DEFAULTS = {
  email: "",
  phone: "",
  firstName: "",
  lastName: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  items: '[{"name":"Life OS Planner","qty":1,"priceINR":999}]',
};

const PRODUCT_FORM_DEFAULTS = {
  id: "",
  name: "",
  slug: "",
  category: "planners",
  plannerType: "undated",
  priceINR: 999,
  compareAtINR: "",
  status: "active",
  stockStatus: "in_stock",
  stockQty: 25,
  badge: "",
  description: "",
  bullets: "",
  inside: "",
  format: "A5",
  cover: "Soft Paperback",
  personalization: "",
  pageColors: "",
  colors: "Cream",
  variantPricing: "",
  features: "undated",
  images: "",
  rating: 4.9,
  reviews: 0,
};

const ts = (value) => (value?.seconds ? new Date(value.seconds * 1000).toLocaleString() : "N/A");
const dateOnly = (value) => (value?.seconds ? new Date(value.seconds * 1000).toLocaleDateString() : "N/A");
const clean = (value) => String(value ?? "").replace(/"/g, '""');
const shortId = (id = "") => id.slice(0, 8).toUpperCase();
const statusLabel = (value = "") => value.replace(/_/g, " ");
const slugify = (value = "") => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const listFromText = (value = "") => String(value).split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
const textFromList = (value) => Array.isArray(value) ? value.join("\n") : value || "";
const paidStatuses = new Set(["paid", "captured"]);
const isPaidOrder = (order) => paidStatuses.has(String(order.paymentStatus || "").toLowerCase()) && (order.total || 0) > 0;
const orderEmail = (order) => (order.emailLower || order.email || "").toLowerCase();
const addressLine = (address = {}) =>
  [address.address, address.landmark, address.city, address.state, address.pincode, address.country].filter(Boolean).join(", ");
const itemOptionLine = (item = {}) =>
  [
    item.name,
    `Qty ${item.qty || 1}`,
    item.size,
    item.cover,
    item.personalization,
    item.plannerName ? `Name: ${item.plannerName}` : "",
    item.pageColor,
    item.color,
  ].filter(Boolean).join(" | ");
const joinOrderItems = (orders, selector) =>
  orders.flatMap((order) => (order.items || []).map(selector)).filter(Boolean).join("; ");
const jsonFromText = (value = "") => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    throw new Error("Variant pricing must be valid JSON");
  }
};

const csvDownload = (filename, rows) => {
  if (!rows.length) return toast.error("No rows to export");
  const headers = Object.keys(rows[0]);
  const body = rows.map((row) => headers.map((header) => `"${clean(row[header])}"`).join(","));
  const blob = new Blob([[headers.join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const makeCouponCode = (discountPercent = 100) => `NC${discountPercent}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

const Panel = ({ children, className = "" }) => (
  <div className={`bg-cream border border-line rounded-xl ${className}`}>{children}</div>
);

const Pill = ({ children, tone = "neutral" }) => {
  const tones = {
    neutral: "bg-ivory text-muted_brown border border-line",
    good: "bg-emerald-50 text-emerald-700",
    warn: "bg-champagne text-espresso",
    bad: "bg-red-50 text-red-700",
    dark: "bg-espresso text-cream",
  };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs capitalize ${tones[tone]}`}>{children}</span>;
};

const StatCard = ({ icon: Icon, label, value, sub, tone = "neutral" }) => {
  const tones = {
    neutral: "bg-ivory text-espresso",
    gold: "bg-champagne text-espresso",
    green: "bg-emerald-50 text-emerald-700",
    blue: "bg-sky-50 text-sky-700",
  };
  return (
    <Panel className="p-5">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tones[tone]}`}>
        <Icon size={18} strokeWidth={1.7} />
      </div>
      <div className="font-serif text-3xl text-espresso mt-4">{value}</div>
      <div className="text-xs uppercase tracking-[0.16em] text-muted_brown mt-1">{label}</div>
      {sub && <div className="text-xs text-muted_brown mt-2">{sub}</div>}
    </Panel>
  );
};

const Empty = ({ children }) => (
  <div className="px-5 py-12 text-center text-muted_brown text-sm">{children}</div>
);

export default function AdminDashboard() {
  const { user, isAdmin, isSuperAdmin, loading, logout } = useAuth();
  const role = isSuperAdmin ? "Super Admin" : "Admin";

  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [bulkInquiries, setBulkInquiries] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [productReviews, setProductReviews] = useState([]);
  const [controls, setControls] = useState(CONTROL_DEFAULTS);
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [recoveryPaymentId, setRecoveryPaymentId] = useState("");
  const [recoveryNeedsDetails, setRecoveryNeedsDetails] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState(RECOVERY_FORM_DEFAULTS);
  const [recoveringPayment, setRecoveringPayment] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: "",
    assignedName: "",
    assignedEmail: "",
    expiresAt: "",
    status: "active",
    discountPercent: 100,
    maxUses: 1,
  });
  const [productForm, setProductForm] = useState(PRODUCT_FORM_DEFAULTS);
  const [uploadingImage, setUploadingImage] = useState(false);

  const loadAll = async () => {
    setBusy(true);
    try {
      const sortDesc = (a, b) => (b.createdAt?.seconds || b.timestamp?.seconds || 0) - (a.createdAt?.seconds || a.timestamp?.seconds || 0);
      const readRows = async (name, source, { sort = true } = {}) => {
        try {
          const snap = await getDocs(source);
          const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          return sort ? rows.sort(sortDesc) : rows;
        } catch (error) {
          console.error(`Could not load ${name}`, error);
          return { failed: true, name, rows: [] };
        }
      };
      const rowsOrEmpty = (result) => Array.isArray(result) ? result : result.rows;

      const [orderRows, userRows, eventRows, unlockRows, contactRows, visitorRows, bulkRows, newsletterRows, couponRows, productRows, reviewRows, controlRows] = await Promise.all([
        readRows("orders", collection(db, "orders")),
        readRows("users", collection(db, "users")),
        readRows("events", query(collection(db, "events"), orderBy("timestamp", "desc"), limit(300)), { sort: false }),
        readRows("digital unlock requests", collection(db, "digital_unlock_requests")),
        readRows("contact requests", collection(db, "contact_requests")),
        readRows("checkout visitors", collection(db, "checkout_visitors")),
        readRows("bulk inquiries", collection(db, "bulk_inquiries")),
        readRows("newsletter subscribers", collection(db, "newsletter_subscribers")),
        readRows("coupons", collection(db, "coupons")),
        readRows("products", collection(db, "products")),
        readRows("product reviews", collection(db, "product_reviews")),
        readRows("platform controls", collection(db, "platform_controls")),
      ]);

      setOrders(rowsOrEmpty(orderRows));
      setUsers(rowsOrEmpty(userRows));
      setEvents(rowsOrEmpty(eventRows));
      setUnlocks(rowsOrEmpty(unlockRows));
      setContacts(rowsOrEmpty(contactRows));
      setVisitors(rowsOrEmpty(visitorRows));
      setBulkInquiries(rowsOrEmpty(bulkRows));
      setNewsletter(rowsOrEmpty(newsletterRows));
      setCoupons(rowsOrEmpty(couponRows));
      setProducts(rowsOrEmpty(productRows));
      setProductReviews(rowsOrEmpty(reviewRows));
      const globalControls = rowsOrEmpty(controlRows).find((d) => d.id === "global");
      setControls({ ...CONTROL_DEFAULTS, ...(globalControls || {}) });

      const failed = [orderRows, userRows, eventRows, unlockRows, contactRows, visitorRows, bulkRows, newsletterRows, couponRows, productRows, reviewRows, controlRows].filter((result) => result.failed);
      if (failed.length) {
        toast.error(`Some admin data could not load: ${failed.map((result) => result.name).join(", ")}.`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Could not load admin data. Check Firestore rules for admin access.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const stats = useMemo(() => {
    const paidOrders = orders.filter(isPaidOrder);
    const paidEmails = new Set(paidOrders.map(orderEmail).filter(Boolean));
    const couponEmails = new Set(orders.filter((order) => order.couponCode).map(orderEmail).filter(Boolean));
    const revenue = paidOrders.reduce((sum, order) => sum + (order.total || 0), 0);
    const delivered = orders.filter((o) => o.status === "delivered").length;
    const activeCoupons = coupons.filter((c) => c.status === "active").length;
    const pendingOps = orders.filter((o) => ["pending", "confirmed", "accepted", "packed"].includes(o.status || "confirmed")).length;
    const buyClicks = events.filter((e) => e.type === "buy_now_click" || e.type === "add_to_cart").length;
    const conversion = buyClicks ? ((paidOrders.length / buyClicks) * 100).toFixed(1) : "0.0";
    return { revenue, delivered, activeCoupons, pendingOps, conversion, paidCustomerCount: paidEmails.size, couponCustomerCount: couponEmails.size };
  }, [orders, events, coupons]);

  const customerOrders = (customer) => {
    const email = (customer?.email || "").toLowerCase();
    return orders.filter((order) => order.userId === customer?.id || (order.email || "").toLowerCase() === email || (order.emailLower || "").toLowerCase() === email);
  };

  const filteredOrders = useMemo(() => {
    const q = search.toLowerCase();
    return orders.filter((order) => {
      const matchesStatus = statusFilter === "all" || (order.status || "confirmed") === statusFilter;
      const haystack = [
        order.id,
        order.email,
        order.paymentMethod,
        order.paymentStatus,
        order.status,
        order.trackingNumber,
        order.shippingAddress?.firstName,
        order.shippingAddress?.lastName,
        order.shippingAddress?.phone,
        order.shippingAddress?.city,
        order.shippingAddress?.pincode,
      ].join(" ").toLowerCase();
      return matchesStatus && (!q || haystack.includes(q));
    });
  }, [orders, search, statusFilter]);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((customer) => {
      const ordersForCustomer = customerOrders(customer);
      const haystack = [customer.name, customer.email, customer.phone, customer.id, ordersForCustomer.map((o) => o.id).join(" ")].join(" ").toLowerCase();
      return !q || haystack.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, orders, search]);

  const filteredCoupons = useMemo(() => {
    const q = search.toLowerCase();
    return coupons.filter((coupon) => [coupon.code, coupon.assignedName, coupon.assignedEmail, coupon.status, coupon.usedByEmail].join(" ").toLowerCase().includes(q));
  }, [coupons, search]);

  const updateOrder = async (orderId, patch) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { ...patch, updatedAt: serverTimestamp(), updatedBy: user.email });
      toast.success("Order updated");
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error("Could not update order");
    }
  };

  const recoverPayment = async () => {
    const paymentId = recoveryPaymentId.trim();
    if (!paymentId) return toast.error("Enter a Razorpay payment ID");
    setRecoveringPayment(true);
    try {
      let manualOrder = null;
      if (recoveryNeedsDetails) {
        const items = JSON.parse(recoveryForm.items || "[]");
        manualOrder = {
          email: recoveryForm.email,
          phone: recoveryForm.phone,
          firstName: recoveryForm.firstName,
          lastName: recoveryForm.lastName,
          address: recoveryForm.address,
          city: recoveryForm.city,
          state: recoveryForm.state,
          pincode: recoveryForm.pincode,
          items,
        };
      }
      const recoverRazorpayPayment = httpsCallable(functions, "recoverRazorpayPayment");
      const result = await recoverRazorpayPayment({ razorpayPaymentId: paymentId, manualOrder });
      toast.success(result.data?.duplicate ? "Payment already has an order" : "Recovered paid order");
      setRecoveryPaymentId("");
      setRecoveryForm(RECOVERY_FORM_DEFAULTS);
      setRecoveryNeedsDetails(false);
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Could not recover payment");
    } finally {
      setRecoveringPayment(false);
    }
  };

  const decideUnlock = async (req, decision) => {
    try {
      await updateDoc(doc(db, "digital_unlock_requests", req.id), { status: decision, decidedAt: serverTimestamp(), decidedBy: user.email });
      if (req.userId) {
        await setDoc(
          doc(db, "users", req.userId),
          decision === "approved"
            ? { digitalAccess: "approved", digitalApprovedAt: serverTimestamp(), digitalApprovedBy: user.email }
            : { digitalAccess: "denied", digitalDeniedAt: serverTimestamp(), digitalDeniedBy: user.email },
          { merge: true }
        );
      }
      toast.success(`Request ${decision}`);
      loadAll();
    } catch {
      toast.error("Could not update request");
    }
  };

  const saveControls = async (next) => {
    if (!isSuperAdmin) return toast.error("Only super admin can change platform controls");
    try {
      await setDoc(doc(db, "platform_controls", "global"), { ...next, updatedAt: serverTimestamp(), updatedBy: user.email }, { merge: true });
      setControls(next);
      toast.success("Platform controls updated");
    } catch (error) {
      console.error(error);
      toast.error("Could not update controls");
    }
  };

  const createCoupon = async (override = {}) => {
    if (!isSuperAdmin) return toast.error("Only super admin can create coupons");
    const code = (override.code || couponForm.code || makeCouponCode()).trim().toUpperCase();
    try {
      await setDoc(doc(db, "coupons", code), {
        code,
        type: "percent",
        discountPercent: Number(override.discountPercent || couponForm.discountPercent || 100),
        maxUses: Number(override.maxUses || couponForm.maxUses || 1),
        usedCount: 0,
        status: override.status || couponForm.status || "active",
        assignedName: override.assignedName ?? couponForm.assignedName,
        assignedEmail: (override.assignedEmail ?? couponForm.assignedEmail).trim().toLowerCase(),
        expiresAt: override.expiresAt ?? couponForm.expiresAt,
        createdAt: serverTimestamp(),
        createdBy: user.email,
      });
      toast.success(`Coupon ${code} created`);
      setCouponForm({ code: "", assignedName: "", assignedEmail: "", expiresAt: "", status: "active", discountPercent: 100, maxUses: 1 });
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error("Could not create coupon");
    }
  };

  const generateCoupons = async (count, discountPercent = 100) => {
    if (!isSuperAdmin) return toast.error("Only super admin can generate coupons");
    try {
      await Promise.all(Array.from({ length: count }, () => {
        const code = makeCouponCode(discountPercent);
        return setDoc(doc(db, "coupons", code), {
          code,
          type: "percent",
          discountPercent,
          maxUses: 1,
          usedCount: 0,
          status: "active",
          assignedName: "",
          assignedEmail: "",
          expiresAt: "",
          createdAt: serverTimestamp(),
          createdBy: user.email,
        });
      }));
      toast.success(`${count} ${discountPercent}% off coupons generated`);
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error("Could not generate coupons");
    }
  };

  const updateCoupon = async (coupon, patch) => {
    if (!isSuperAdmin) return toast.error("Only super admin can update coupons");
    try {
      await updateDoc(doc(db, "coupons", coupon.id), { ...patch, updatedAt: serverTimestamp(), updatedBy: user.email });
      toast.success("Coupon updated");
      loadAll();
    } catch {
      toast.error("Could not update coupon");
    }
  };

  const deleteCoupon = async (coupon) => {
    if (!isSuperAdmin) return toast.error("Only super admin can delete coupons");
    try {
      await deleteDoc(doc(db, "coupons", coupon.id));
      toast.success("Coupon deleted");
      loadAll();
    } catch {
      toast.error("Could not delete coupon");
    }
  };

  const resetProductForm = () => setProductForm(PRODUCT_FORM_DEFAULTS);

  const editProduct = (product) => {
    setProductForm({
      ...PRODUCT_FORM_DEFAULTS,
      id: product.id || "",
      name: product.name || "",
      slug: product.slug || product.id || "",
      category: product.category || "planners",
      plannerType: product.plannerType || "undated",
      priceINR: product.priceINR ?? 999,
      compareAtINR: product.compareAtINR ?? "",
      status: product.status || "active",
      stockStatus: product.stockStatus || "in_stock",
      stockQty: product.stockQty ?? 0,
      badge: product.badge || "",
      description: product.description || "",
      bullets: textFromList(product.bullets),
      inside: textFromList(product.inside),
      format: textFromList(product.format),
      cover: textFromList(product.cover),
      personalization: textFromList(product.personalization),
      pageColors: textFromList(product.pageColors),
      colors: textFromList(product.colors),
      variantPricing: product.variantPricing ? JSON.stringify(product.variantPricing, null, 2) : "",
      features: textFromList(product.features),
      images: textFromList(product.images),
      rating: product.rating ?? 4.9,
      reviews: product.reviews ?? 0,
    });
    setTab("products");
  };

  const productPayload = () => {
    const name = productForm.name.trim();
    const slug = slugify(productForm.slug || name);
    const stockQty = Number(productForm.stockQty || 0);
    return {
      name,
      slug,
      category: productForm.category,
      plannerType: productForm.plannerType,
      priceINR: Number(productForm.priceINR || 0),
      compareAtINR: productForm.compareAtINR === "" ? null : Number(productForm.compareAtINR || 0),
      status: productForm.status,
      stockStatus: productForm.stockStatus,
      stockQty,
      badge: productForm.badge.trim(),
      description: productForm.description.trim(),
      bullets: listFromText(productForm.bullets),
      inside: listFromText(productForm.inside),
      format: listFromText(productForm.format),
      cover: listFromText(productForm.cover),
      personalization: listFromText(productForm.personalization),
      pageColors: listFromText(productForm.pageColors),
      colors: listFromText(productForm.colors),
      variantPricing: jsonFromText(productForm.variantPricing),
      features: listFromText(productForm.features),
      images: String(productForm.images || "").split("\n").map((item) => item.trim()).filter(Boolean),
      rating: Number(productForm.rating || 0),
      reviews: Number(productForm.reviews || 0),
      specs: {
        Category: productForm.category,
        Stock: productForm.stockStatus === "in_stock" ? `${stockQty} available` : "Out of stock",
      },
      updatedAt: serverTimestamp(),
      updatedBy: user.email,
    };
  };

  const saveProduct = async () => {
    if (!isAdmin) return toast.error("Admin access required");
    try {
      const payload = productPayload();
      if (!payload.name || !payload.slug) return toast.error("Product name is required");
      const id = productForm.id || payload.slug;
      const existing = products.find((product) => product.id === id);
      await setDoc(
        doc(db, "products", id),
        {
          ...payload,
          id,
          createdAt: existing?.createdAt || serverTimestamp(),
          createdBy: existing?.createdBy || user.email,
        },
        { merge: true }
      );
      toast.success(productForm.id ? "Product updated" : "Product added");
      resetProductForm();
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Could not save product");
    }
  };

  const updateProduct = async (product, patch) => {
    if (!isAdmin) return toast.error("Admin access required");
    try {
      await updateDoc(doc(db, "products", product.id), { ...patch, updatedAt: serverTimestamp(), updatedBy: user.email });
      toast.success("Product updated");
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error("Could not update product");
    }
  };

  const deleteProduct = async (product) => {
    if (!isSuperAdmin) return toast.error("Only super admin can delete products");
    try {
      await deleteDoc(doc(db, "products", product.id));
      toast.success("Product deleted");
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error("Could not delete product");
    }
  };

  const updateReview = async (review, patch) => {
    if (!isAdmin) return toast.error("Admin access required");
    try {
      await updateDoc(doc(db, "product_reviews", review.id), { ...patch, updatedAt: serverTimestamp(), updatedBy: user.email });
      toast.success("Review updated");
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error("Could not update review");
    }
  };

  const deleteReview = async (review) => {
    if (!isSuperAdmin) return toast.error("Only super admin can delete reviews");
    try {
      await deleteDoc(doc(db, "product_reviews", review.id));
      toast.success("Review deleted");
      loadAll();
    } catch (error) {
      console.error(error);
      toast.error("Could not delete review");
    }
  };

  const uploadProductImage = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) return toast.error("Upload an image file");
    setUploadingImage(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
      const imageRef = storageRef(storage, `products/${Date.now()}-${safeName}`);
      await uploadBytes(imageRef, file, { contentType: file.type });
      const url = await getDownloadURL(imageRef);
      setProductForm((current) => ({ ...current, images: [current.images, url].filter(Boolean).join("\n") }));
      toast.success("Product image uploaded");
    } catch (error) {
      console.error(error);
      toast.error("Could not upload image. Check Firebase Storage rules.");
    } finally {
      setUploadingImage(false);
    }
  };

  const exportRows = (kind) => {
    const maps = {
      orders: orders.map((o) => ({
        id: o.id,
        email: o.email,
        name: `${o.shippingAddress?.firstName || ""} ${o.shippingAddress?.lastName || ""}`.trim(),
        phone: o.shippingAddress?.phone || o.phone || "",
        city: o.shippingAddress?.city || "",
        pincode: o.shippingAddress?.pincode || "",
        items: (o.items || []).map(itemOptionLine).join("; "),
        itemSizes: (o.items || []).map((i) => i.size || "").filter(Boolean).join("; "),
        itemCovers: (o.items || []).map((i) => i.cover || "").filter(Boolean).join("; "),
        itemPersonalization: (o.items || []).map((i) => i.personalization || "").filter(Boolean).join("; "),
        plannerNames: (o.items || []).map((i) => i.plannerName || "").filter(Boolean).join("; "),
        pageColours: (o.items || []).map((i) => i.pageColor || "").filter(Boolean).join("; "),
        coverColours: (o.items || []).map((i) => i.color || "").filter(Boolean).join("; "),
        paymentMethod: o.paymentMethod || "",
        paymentStatus: o.paymentStatus || "",
        couponCode: o.couponCode || "",
        couponDiscount: o.couponDiscount || 0,
        shipping: o.shipping || 0,
        tax: o.tax || 0,
        paidCustomer: isPaidOrder(o) ? "yes" : "no",
        total: o.total || 0,
        status: o.status || "confirmed",
        trackingProvider: o.trackingProvider || "",
        trackingNumber: o.trackingNumber || "",
        createdAt: ts(o.createdAt),
      })),
      customers: users.map((u) => {
        const co = customerOrders(u);
        const paid = co.filter(isPaidOrder);
        const latestOrder = co[0] || {};
        const latestAddress = latestOrder.shippingAddress || {};
        return {
          id: u.id,
          name: u.name || `${latestAddress.firstName || ""} ${latestAddress.lastName || ""}`.trim(),
          email: u.email || latestOrder.email || "",
          phone: u.phone || latestAddress.phone || "",
          address: addressLine(latestAddress),
          city: latestAddress.city || "",
          state: latestAddress.state || "",
          pincode: latestAddress.pincode || "",
          role: u.isAdmin ? "admin" : "customer",
          digitalAccess: u.digitalAccess || "",
          paidOrders: paid.length,
          couponOrders: co.filter((o) => o.couponCode).length,
          paidSpend: paid.reduce((s, o) => s + (o.total || 0), 0),
          orderIds: co.map((o) => o.id).join("; "),
          orderStatuses: co.map((o) => o.status || "confirmed").join("; "),
          paymentIds: co.map((o) => o.razorpayPaymentId || "").filter(Boolean).join("; "),
          paymentMethods: co.map((o) => o.paymentMethod || "").filter(Boolean).join("; "),
          paymentStatuses: co.map((o) => o.paymentStatus || "").filter(Boolean).join("; "),
          couponCodes: co.map((o) => o.couponCode || "").filter(Boolean).join("; "),
          chosenPlanners: joinOrderItems(co, (i) => i.name),
          quantities: joinOrderItems(co, (i) => i.qty ? `Qty ${i.qty}` : ""),
          sizes: joinOrderItems(co, (i) => i.size),
          covers: joinOrderItems(co, (i) => i.cover),
          personalization: joinOrderItems(co, (i) => i.personalization),
          printedNames: joinOrderItems(co, (i) => i.plannerName),
          pageColours: joinOrderItems(co, (i) => i.pageColor),
          coverColours: joinOrderItems(co, (i) => i.color),
          itemDetails: co.map((o) => `#${shortId(o.id)}: ${(o.items || []).map(itemOptionLine).join(" / ")}`).join("; "),
          joined: ts(u.createdAt),
          latestOrderAt: ts(latestOrder.createdAt),
        };
      }),
      payments: orders.map((o) => ({ orderId: o.id, email: o.email, method: o.paymentMethod || "", status: o.paymentStatus || o.status || "", paidCustomer: isPaidOrder(o) ? "yes" : "no", total: o.total || 0, couponCode: o.couponCode || "", createdAt: ts(o.createdAt) })),
      coupons: coupons.map((c) => ({ code: c.code, status: c.status, discountPercent: c.discountPercent, assignedEmail: c.assignedEmail, usedByEmail: c.usedByEmail, usedCount: c.usedCount || 0, maxUses: c.maxUses || 1, createdAt: ts(c.createdAt) })),
      products: products.map((p) => ({ id: p.id, name: p.name, slug: p.slug, category: p.category, priceINR: p.priceINR, status: p.status, stockStatus: p.stockStatus, stockQty: p.stockQty, rating: p.rating, reviews: p.reviews, createdAt: ts(p.createdAt) })),
      reviews: productReviews.map((r) => ({ id: r.id, productId: r.productId, name: r.name, email: r.email, rating: r.rating, status: r.status, review: r.review || r.message, createdAt: ts(r.createdAt) })),
      contacts: contacts.map((c) => ({ name: c.name, email: c.email, phone: c.phone, subject: c.subject, message: c.message, status: c.status, createdAt: ts(c.createdAt) })),
      visitors: visitors.map((v) => ({ email: v.email, phone: v.phone, firstName: v.firstName, lastName: v.lastName, city: v.city, pincode: v.pincode, plannerName: v.plannerName, items: (v.items || []).map(itemOptionLine).join("; "), subtotal: v.subtotal || 0, source: v.source, status: v.status, updatedAt: ts(v.updatedAt) })),
    };
    csvDownload(`note-console-${kind}-${Date.now()}.csv`, maps[kind] || []);
  };

  const nav = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "customers", label: "Customers", icon: Users },
    { key: "orders", label: "Orders", icon: ShoppingBag },
    { key: "products", label: "Products", icon: Package },
    { key: "reviews", label: "Reviews", icon: Star },
    { key: "payments", label: "Payments", icon: CreditCard },
    { key: "coupons", label: "Coupons", icon: TicketPercent, superOnly: true },
    { key: "unlocks", label: "Digital Access", icon: KeyRound },
    { key: "contacts", label: "Messages", icon: MessageSquare },
    { key: "visitors", label: "Visitors", icon: Eye },
    { key: "bulk", label: "Bulk Leads", icon: ClipboardList },
    { key: "newsletter", label: "Newsletter", icon: Mail },
    { key: "events", label: "Activity", icon: Activity },
    { key: "exports", label: "Export Data", icon: FileDown },
    { key: "controls", label: "Controls", icon: Settings2, superOnly: true },
  ];

  if (loading) return <div className="container-nc py-20 text-muted_brown">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#f5f7fb] text-slate-900">
      <div className="grid lg:grid-cols-[320px_1fr]">
        <aside className="bg-white border-r border-slate-200 lg:min-h-screen lg:sticky lg:top-0 self-start">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <img src="/favicon.png" alt="Note Console" className="w-12 h-12 rounded-xl object-cover bg-slate-100" />
              <div>
                <div className="font-serif text-2xl text-espresso">Note Console</div>
                <div className="text-xs text-slate-500">Operations Console</div>
              </div>
            </div>
          </div>
          <nav className="p-4 space-y-1">
            {nav.filter((item) => !item.superOnly || isSuperAdmin).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`admin-tab-${key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${
                  tab === key ? "bg-espresso text-cream shadow-soft" : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                }`}
              >
                <Icon size={17} strokeWidth={1.8} /> {label}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-slate-200 lg:absolute lg:bottom-0 lg:left-0 lg:right-0 bg-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-espresso text-cream flex items-center justify-center font-medium">{user.email?.[0]?.toUpperCase()}</div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">{role}</div>
                <div className="text-xs text-slate-500 truncate">{user.email}</div>
              </div>
            </div>
            <button onClick={logout} data-testid="admin-logout" className="w-full border border-slate-200 rounded-xl py-2.5 text-sm flex items-center justify-center gap-2 hover:bg-slate-50">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </aside>

        <main className="p-5 md:p-8 xl:p-10">
          <div className="flex flex-wrap gap-4 items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-blue-600 font-semibold">Note Console Admin</p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-2">{nav.find((n) => n.key === tab)?.label || "Dashboard"}</h1>
              <p className="text-slate-500 mt-2">Full control for customers, orders, payments, coupons, exports, and platform access.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={loadAll} className="btn-outline bg-white text-xs py-2.5 px-4"><RefreshCw size={14} /> Refresh</button>
              <Link to="/" className="btn-primary text-xs py-2.5 px-4">Storefront</Link>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-3 mb-6">
            <Search size={17} className="text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers, orders, payments, coupons, emails..." data-testid="admin-search" className="flex-1 bg-transparent outline-none text-sm" />
          </div>

          {busy && <Panel className="p-10 text-center text-muted_brown">Loading operations data...</Panel>}

          {!busy && tab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                <StatCard icon={IndianRupee} label="Paid Revenue" value={formatINR(stats.revenue)} sub={`${orders.filter(isPaidOrder).length} paid orders`} tone="green" />
                <StatCard icon={Users} label="Paid Customers" value={stats.paidCustomerCount} sub={`${stats.couponCustomerCount} used coupons`} tone="blue" />
                <StatCard icon={Truck} label="Delivered" value={stats.delivered} sub={`${stats.pendingOps} active ops`} tone="gold" />
                <StatCard icon={TicketPercent} label="Active Coupons" value={stats.activeCoupons} sub={`${coupons.length} total`} />
                <StatCard icon={Package} label="Products" value={products.length + PRODUCTS.length} sub={`${products.filter((p) => p.stockStatus === "out_of_stock").length} sold out`} />
              </div>
              <Panel className="overflow-hidden">
                <div className="p-5 border-b border-line flex items-center gap-2"><ShoppingBag size={18} className="text-gold" /><h2 className="font-serif text-2xl text-espresso">Recent Orders</h2></div>
                <OrderTable orders={orders.slice(0, 8)} updateOrder={updateOrder} compact />
              </Panel>
            </div>
          )}

          {!busy && tab === "orders" && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {["all", ...ORDER_STATUSES].map((status) => (
                  <button key={status} onClick={() => setStatusFilter(status)} className={`px-4 py-2 rounded-xl text-sm border ${statusFilter === status ? "bg-espresso text-cream border-espresso" : "bg-white border-slate-200 text-slate-600"}`}>{statusLabel(status)}</button>
                ))}
              </div>
              <Panel className="overflow-hidden"><OrderTable orders={filteredOrders} updateOrder={updateOrder} /></Panel>
            </div>
          )}

          {!busy && tab === "customers" && (
            <div className="grid xl:grid-cols-[1fr_420px] gap-5">
              <Panel className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                    <tr><th className="px-5 py-4">Customer</th><th className="px-5 py-4">Phone</th><th className="px-5 py-4">Latest Address</th><th className="px-5 py-4">Orders</th><th className="px-5 py-4">Spend</th><th className="px-5 py-4">Joined</th><th className="px-5 py-4">Action</th></tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((customer) => {
                      const co = customerOrders(customer);
                      const paid = co.filter(isPaidOrder);
                      const latestAddress = co[0]?.shippingAddress || {};
                      return (
                        <tr key={customer.id} className="border-t border-line align-top">
                          <td className="px-5 py-4"><div className="font-medium text-espresso">{customer.name || "Guest"}</div><div className="text-xs text-slate-500">{customer.email}</div></td>
                          <td className="px-5 py-4 text-slate-600">{customer.phone || latestAddress.phone || "N/A"}</td>
                          <td className="px-5 py-4 text-xs text-slate-500 max-w-[260px]">{addressLine(latestAddress) || "N/A"}</td>
                          <td className="px-5 py-4">{paid.length}</td>
                          <td className="px-5 py-4">{formatINR(paid.reduce((s, o) => s + (o.total || 0), 0))}</td>
                          <td className="px-5 py-4 text-slate-500">{dateOnly(customer.createdAt)}</td>
                          <td className="px-5 py-4"><button onClick={() => setSelectedCustomer(customer)} className="btn-outline text-xs py-2 px-4"><Eye size={13} /> View</button></td>
                        </tr>
                      );
                    })}
                    {filteredUsers.length === 0 && <tr><td colSpan={7}><Empty>No customers found.</Empty></td></tr>}
                  </tbody>
                </table>
              </Panel>
              <CustomerPanel customer={selectedCustomer || filteredUsers[0]} orders={customerOrders(selectedCustomer || filteredUsers[0])} />
            </div>
          )}

          {!busy && tab === "products" && (
            <div className="space-y-5">
              <Panel className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <div>
                    <h2 className="font-serif text-3xl text-espresso">Shop Product Control</h2>
                    <p className="text-sm text-slate-500 mt-1">Add planners, upload images, manage stock, and decide what appears on the storefront.</p>
                  </div>
                  <button onClick={resetProductForm} className="btn-outline bg-white text-xs py-2.5 px-4">New Product</button>
                </div>
                <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} placeholder="Product name" className="input-nc bg-white" />
                  <input value={productForm.slug} onChange={(e) => setProductForm({ ...productForm, slug: e.target.value })} placeholder="URL slug" className="input-nc bg-white" />
                  <input type="number" value={productForm.priceINR} onChange={(e) => setProductForm({ ...productForm, priceINR: e.target.value })} placeholder="Price INR" className="input-nc bg-white" />
                  <input type="number" value={productForm.compareAtINR} onChange={(e) => setProductForm({ ...productForm, compareAtINR: e.target.value })} placeholder="Compare price" className="input-nc bg-white" />
                  <select value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} className="input-nc bg-white">
                    <option value="planners">Planners</option>
                  </select>
                  <select value={productForm.status} onChange={(e) => setProductForm({ ...productForm, status: e.target.value })} className="input-nc bg-white">
                    <option value="active">Show on shop</option>
                    <option value="hidden">Hide from shop</option>
                    <option value="draft">Draft</option>
                  </select>
                  <select value={productForm.stockStatus} onChange={(e) => setProductForm({ ...productForm, stockStatus: e.target.value })} className="input-nc bg-white">
                    <option value="in_stock">In stock</option>
                    <option value="out_of_stock">Out of stock</option>
                    <option value="preorder">Preorder</option>
                  </select>
                  <input type="number" value={productForm.stockQty} onChange={(e) => setProductForm({ ...productForm, stockQty: e.target.value })} placeholder="Stock quantity" className="input-nc bg-white" />
                  <input value={productForm.badge} onChange={(e) => setProductForm({ ...productForm, badge: e.target.value })} placeholder="Badge, e.g. Bestseller" className="input-nc bg-white" />
                  <input value={productForm.format} onChange={(e) => setProductForm({ ...productForm, format: e.target.value })} placeholder="Formats: A5, B5" className="input-nc bg-white" />
                  <input value={productForm.cover} onChange={(e) => setProductForm({ ...productForm, cover: e.target.value })} placeholder="Covers" className="input-nc bg-white" />
                  <input value={productForm.personalization} onChange={(e) => setProductForm({ ...productForm, personalization: e.target.value })} placeholder="Personalization options" className="input-nc bg-white" />
                  <input value={productForm.pageColors} onChange={(e) => setProductForm({ ...productForm, pageColors: e.target.value })} placeholder="Page colours: B/W, Coloured" className="input-nc bg-white" />
                  <input value={productForm.colors} onChange={(e) => setProductForm({ ...productForm, colors: e.target.value })} placeholder="Colors" className="input-nc bg-white" />
                  <textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} placeholder="Premium product description" className="input-nc bg-white md:col-span-2 min-h-[120px]" />
                  <textarea value={productForm.bullets} onChange={(e) => setProductForm({ ...productForm, bullets: e.target.value })} placeholder="Bullet points, one per line" className="input-nc bg-white min-h-[120px]" />
                  <textarea value={productForm.inside} onChange={(e) => setProductForm({ ...productForm, inside: e.target.value })} placeholder="Inside pages/features, one per line" className="input-nc bg-white min-h-[120px]" />
                  <textarea value={productForm.features} onChange={(e) => setProductForm({ ...productForm, features: e.target.value })} placeholder="Tags/features, one per line" className="input-nc bg-white min-h-[120px]" />
                  <textarea
                    value={productForm.variantPricing}
                    onChange={(e) => setProductForm({ ...productForm, variantPricing: e.target.value })}
                    placeholder="Variant pricing JSON"
                    className="input-nc bg-white min-h-[120px]"
                  />
                  <textarea value={productForm.images} onChange={(e) => setProductForm({ ...productForm, images: e.target.value })} placeholder="Image URLs, one per line" className="input-nc bg-white min-h-[120px]" />
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-5">
                  <button
                    type="button"
                    onClick={() => setProductForm({
                      ...productForm,
                      format: "B5",
                      cover: "Soft Paperback\nHard Binding",
                      personalization: "Without Name\nWith Name on Cover",
                      pageColors: "B/W\nColoured",
                      priceINR: 349,
                      variantPricing: JSON.stringify(LIFE_OS_VARIANT_PRICING, null, 2),
                    })}
                    className="btn-outline bg-white text-xs py-2.5 px-4"
                  >
                    Load Life OS Pricing
                  </button>
                  <label className="btn-outline bg-white text-xs py-2.5 px-4 cursor-pointer">
                    <Package size={14} /> {uploadingImage ? "Uploading..." : "Upload Product Image"}
                    <input type="file" accept="image/*" onChange={(e) => uploadProductImage(e.target.files?.[0])} className="hidden" disabled={uploadingImage} />
                  </label>
                  <button onClick={saveProduct} className="btn-primary text-xs py-2.5 px-5">{productForm.id ? "Save Product" : "Add Product"}</button>
                  <div className="text-xs text-slate-500">Admins can create/update. Only super admin can permanently delete.</div>
                </div>
              </Panel>

              <Panel className="overflow-hidden">
                <div className="p-5 border-b border-line flex items-center justify-between gap-4">
                  <h2 className="font-serif text-2xl text-espresso">Firestore Products</h2>
                  <Pill tone="neutral">{products.length} managed</Pill>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                      <tr><th className="px-5 py-4">Product</th><th className="px-5 py-4">Price</th><th className="px-5 py-4">Stock</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Reviews</th><th className="px-5 py-4 text-right">Action</th></tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product.id} className="border-t border-line align-middle">
                          <td className="px-5 py-4 min-w-[280px]">
                            <div className="flex items-center gap-3">
                              <img src={product.images?.[0] || "/product/life-os-planner-closeup.jpg"} alt={product.name} className="w-14 h-14 rounded-xl object-cover border border-line bg-ivory" />
                              <div>
                                <div className="font-medium text-espresso">{product.name}</div>
                                <div className="text-xs text-slate-500">/{product.slug || product.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 font-medium">{formatINR(product.priceINR || 0)}</td>
                          <td className="px-5 py-4 min-w-[160px]">
                            <select value={product.stockStatus || "in_stock"} onChange={(e) => updateProduct(product, { stockStatus: e.target.value })} className="bg-white border border-line rounded-lg px-3 py-2 text-xs capitalize">
                              <option value="in_stock">In stock</option>
                              <option value="out_of_stock">Out of stock</option>
                              <option value="preorder">Preorder</option>
                            </select>
                            <div className="text-xs text-slate-500 mt-1">{product.stockQty || 0} units</div>
                          </td>
                          <td className="px-5 py-4 min-w-[150px]">
                            <select value={product.status || "active"} onChange={(e) => updateProduct(product, { status: e.target.value })} className="bg-white border border-line rounded-lg px-3 py-2 text-xs capitalize">
                              <option value="active">Show</option>
                              <option value="hidden">Hide</option>
                              <option value="draft">Draft</option>
                            </select>
                          </td>
                          <td className="px-5 py-4"><div className="flex items-center gap-1"><Star size={14} className="text-gold fill-gold" /> {product.rating || 0}</div><div className="text-xs text-slate-500">{product.reviews || 0} reviews</div></td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => editProduct(product)} className="btn-outline text-xs py-2 px-3">Edit</button>
                              <button onClick={() => updateProduct(product, { stockStatus: product.stockStatus === "out_of_stock" ? "in_stock" : "out_of_stock" })} className="btn-outline text-xs py-2 px-3">{product.stockStatus === "out_of_stock" ? "Restock" : "Sold Out"}</button>
                              {isSuperAdmin && <button onClick={() => deleteProduct(product)} className="btn-outline text-xs py-2 px-3 text-red-600"><Trash2 size={13} /> Delete</button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {products.length === 0 && <tr><td colSpan={6}><Empty>No Firestore products yet. Add your first planner above.</Empty></td></tr>}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          )}

          {!busy && tab === "reviews" && (
            <Panel className="overflow-hidden">
              <div className="p-5 border-b border-line flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-serif text-2xl text-espresso">Product Reviews</h2>
                  <p className="text-sm text-slate-500 mt-1">Approve, hide, or remove storefront reviews.</p>
                </div>
                <Pill tone="neutral">{productReviews.length} reviews</Pill>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                    <tr><th className="px-5 py-4">Review</th><th className="px-5 py-4">Product</th><th className="px-5 py-4">Rating</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Created</th><th className="px-5 py-4 text-right">Action</th></tr>
                  </thead>
                  <tbody>
                    {productReviews.map((review) => (
                      <tr key={review.id} className="border-t border-line align-top">
                        <td className="px-5 py-4 max-w-lg">
                          <div className="font-medium text-espresso">{review.name || "Customer"}</div>
                          <div className="text-xs text-slate-500 mb-2">{review.email || "No email"}</div>
                          <div className="text-slate-600 whitespace-pre-wrap">{review.review || review.message || "No review text"}</div>
                        </td>
                        <td className="px-5 py-4 text-slate-600">{review.productName || review.productId || "N/A"}</td>
                        <td className="px-5 py-4"><div className="flex items-center gap-1"><Star size={14} className="text-gold fill-gold" /> {review.rating || 0}</div></td>
                        <td className="px-5 py-4">
                          <select value={review.status || "pending"} onChange={(e) => updateReview(review, { status: e.target.value })} className="bg-white border border-line rounded-lg px-3 py-2 text-xs capitalize">
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="hidden">Hidden</option>
                            <option value="flagged">Flagged</option>
                          </select>
                        </td>
                        <td className="px-5 py-4 text-slate-500">{ts(review.createdAt)}</td>
                        <td className="px-5 py-4 text-right">
                          {isSuperAdmin ? <button onClick={() => deleteReview(review)} className="btn-outline text-xs py-2 px-3 text-red-600"><Trash2 size={13} /> Delete</button> : <span className="text-xs text-slate-500">Moderate only</span>}
                        </td>
                      </tr>
                    ))}
                    {productReviews.length === 0 && <tr><td colSpan={6}><Empty>No reviews yet.</Empty></td></tr>}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {!busy && tab === "payments" && (
            <div className="space-y-5">
              <Panel className="p-5">
                <h2 className="font-serif text-2xl text-espresso mb-4">Recover Razorpay Payment</h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input value={recoveryPaymentId} onChange={(e) => setRecoveryPaymentId(e.target.value)} placeholder="pay_XXXXXXXXXXXXXX" className="input-nc bg-white flex-1" />
                  <button disabled={recoveringPayment} onClick={recoverPayment} className="btn-primary disabled:opacity-60">{recoveringPayment ? "Recovering..." : "Recover Order"}</button>
                </div>
                <label className="flex items-center gap-2 mt-4 text-sm text-espresso cursor-pointer">
                  <input type="checkbox" checked={recoveryNeedsDetails} onChange={(e) => setRecoveryNeedsDetails(e.target.checked)} className="accent-espresso" />
                  I need to enter missing customer/order details
                </label>
                {recoveryNeedsDetails && (
                  <div className="grid md:grid-cols-2 gap-3 mt-4">
                    <input value={recoveryForm.email} onChange={(e) => setRecoveryForm({ ...recoveryForm, email: e.target.value })} placeholder="Customer email" className="input-nc bg-white" />
                    <input value={recoveryForm.phone} onChange={(e) => setRecoveryForm({ ...recoveryForm, phone: e.target.value })} placeholder="Phone" className="input-nc bg-white" />
                    <input value={recoveryForm.firstName} onChange={(e) => setRecoveryForm({ ...recoveryForm, firstName: e.target.value })} placeholder="First name" className="input-nc bg-white" />
                    <input value={recoveryForm.lastName} onChange={(e) => setRecoveryForm({ ...recoveryForm, lastName: e.target.value })} placeholder="Last name" className="input-nc bg-white" />
                    <input value={recoveryForm.address} onChange={(e) => setRecoveryForm({ ...recoveryForm, address: e.target.value })} placeholder="Address" className="input-nc bg-white md:col-span-2" />
                    <input value={recoveryForm.city} onChange={(e) => setRecoveryForm({ ...recoveryForm, city: e.target.value })} placeholder="City" className="input-nc bg-white" />
                    <input value={recoveryForm.state} onChange={(e) => setRecoveryForm({ ...recoveryForm, state: e.target.value })} placeholder="State" className="input-nc bg-white" />
                    <input value={recoveryForm.pincode} onChange={(e) => setRecoveryForm({ ...recoveryForm, pincode: e.target.value })} placeholder="PIN code" className="input-nc bg-white" />
                    <textarea value={recoveryForm.items} onChange={(e) => setRecoveryForm({ ...recoveryForm, items: e.target.value })} placeholder='[{"name":"Life OS Planner","qty":1,"priceINR":999}]' className="input-nc bg-white min-h-28 md:col-span-2 font-mono text-xs" />
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-3">Use this only for paid Razorpay transactions missing from Firebase orders.</p>
              </Panel>
              <Panel className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                    <tr><th className="px-5 py-4">Order</th><th className="px-5 py-4">Customer</th><th className="px-5 py-4">Method</th><th className="px-5 py-4">Coupon</th><th className="px-5 py-4">Amount</th><th className="px-5 py-4">Status</th></tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="border-t border-line">
                        <td className="px-5 py-4 font-medium">#{shortId(order.id)}</td>
                        <td className="px-5 py-4 text-slate-600">{order.email}</td>
                        <td className="px-5 py-4 uppercase text-xs">{order.paymentMethod || "N/A"}</td>
                        <td className="px-5 py-4">{order.couponCode || "N/A"}</td>
                        <td className="px-5 py-4 font-medium">{formatINR(order.total || 0)}</td>
                        <td className="px-5 py-4"><Pill tone={(order.paymentStatus || order.status) === "paid" || order.status === "confirmed" ? "good" : "warn"}>{order.paymentStatus || order.status || "confirmed"}</Pill></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </div>
          )}

          {!busy && tab === "coupons" && (
            <div className="space-y-5">
              <Panel className="p-5">
                <h2 className="font-serif text-2xl text-espresso mb-4">Quick Coupon Generator</h2>
                <div className="space-y-3">
                  {[30, 40, 50, 90, 100].map((percent) => (
                    <div key={percent} className="flex flex-wrap items-center gap-2">
                      <div className="w-20 text-sm font-medium text-espresso">{percent}% off</div>
                      {[10, 20, 50, 100].map((count) => (
                        <button key={`${percent}-${count}`} onClick={() => generateCoupons(count, percent)} className="btn-outline text-xs py-2 px-3">
                          <TicketPercent size={13} /> Generate {count}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </Panel>
              <Panel className="p-5">
                <h2 className="font-serif text-2xl text-espresso mb-5">Add Manual Coupon</h2>
                <div className="grid md:grid-cols-3 gap-4">
                  <input value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value })} placeholder="Auto-generate" className="input-nc bg-white" />
                  <input value={couponForm.assignedName} onChange={(e) => setCouponForm({ ...couponForm, assignedName: e.target.value })} placeholder="Assigned name" className="input-nc bg-white" />
                  <input value={couponForm.assignedEmail} onChange={(e) => setCouponForm({ ...couponForm, assignedEmail: e.target.value })} placeholder="Assigned email" className="input-nc bg-white" />
                  <input type="number" value={couponForm.discountPercent} onChange={(e) => setCouponForm({ ...couponForm, discountPercent: e.target.value })} placeholder="Discount %" className="input-nc bg-white" />
                  <input type="date" value={couponForm.expiresAt} onChange={(e) => setCouponForm({ ...couponForm, expiresAt: e.target.value })} className="input-nc bg-white" />
                  <button onClick={() => createCoupon()} className="btn-primary">Create Coupon</button>
                </div>
              </Panel>
              <Panel className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500"><tr><th className="px-5 py-4">Code</th><th className="px-5 py-4">Assigned</th><th className="px-5 py-4">Value</th><th className="px-5 py-4">Usage</th><th className="px-5 py-4">Status</th><th className="px-5 py-4 text-right">Action</th></tr></thead>
                  <tbody>
                    {filteredCoupons.map((coupon) => (
                      <tr key={coupon.id} className="border-t border-line">
                        <td className="px-5 py-4 font-mono font-semibold">{coupon.code}</td>
                        <td className="px-5 py-4"><div>{coupon.assignedName || "-"}</div><div className="text-xs text-slate-500">{coupon.assignedEmail || "Any email"}</div></td>
                        <td className="px-5 py-4">{coupon.discountPercent || 100}%</td>
                        <td className="px-5 py-4">{coupon.usedCount || 0}/{coupon.maxUses || 1}</td>
                        <td className="px-5 py-4"><Pill tone={coupon.status === "active" ? "good" : coupon.status === "used" ? "warn" : "bad"}>{coupon.status || "active"}</Pill></td>
                        <td className="px-5 py-4 text-right flex gap-2 justify-end">
                          <button onClick={() => navigator.clipboard?.writeText(coupon.code)} className="btn-outline text-xs py-2 px-3">Copy</button>
                          <button onClick={() => updateCoupon(coupon, { status: coupon.status === "active" ? "declined" : "active" })} className="btn-outline text-xs py-2 px-3">{coupon.status === "active" ? "Decline" : "Activate"}</button>
                          <button onClick={() => deleteCoupon(coupon)} className="btn-outline text-xs py-2 px-3 text-red-600"><Trash2 size={13} /> Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </div>
          )}

          {!busy && tab === "unlocks" && <AccessTable unlocks={unlocks} orders={orders} decideUnlock={decideUnlock} />}
          {!busy && tab === "contacts" && <SimpleTable title="Contact Requests" rows={contacts} fields={["name", "email", "phone", "subject", "message", "status"]} />}
          {!busy && tab === "visitors" && (
            <SimpleTable
              title="Visitor Follow-up"
              rows={[
                ...visitors.filter((visitor) => !orders.some((order) => isPaidOrder(order) && orderEmail(order) === (visitor.emailLower || visitor.email || "").toLowerCase())),
                ...users
                  .filter((visitor) => !visitor.isAdmin)
                  .filter((visitor) => !orders.some((order) => isPaidOrder(order) && orderEmail(order) === (visitor.email || "").toLowerCase()))
                  .map((visitor) => ({ ...visitor, source: visitor.source || "signup", status: "signed_up_no_paid_order" })),
              ]}
              fields={["email", "phone", "firstName", "lastName", "city", "pincode", "plannerName", "source", "status"]}
              timeField="updatedAt"
            />
          )}
          {!busy && tab === "bulk" && <SimpleTable title="Bulk Leads" rows={bulkInquiries} fields={["company", "name", "email", "phone", "quantity", "industry", "timeline", "requirements", "status"]} />}
          {!busy && tab === "newsletter" && <SimpleTable title="Newsletter Subscribers" rows={newsletter} fields={["email", "source", "status"]} />}
          {!busy && tab === "events" && <SimpleTable title="Recent Activity" rows={events.slice(0, 150)} fields={["type", "email", "path"]} timeField="timestamp" />}

          {!busy && tab === "exports" && (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {[
                ["orders", "Orders", ShoppingBag],
                ["customers", "Customers", Users],
                ["products", "Products", Package],
                ["reviews", "Reviews", Star],
                ["payments", "Payments", CreditCard],
                ["coupons", "Coupons", TicketPercent],
                ["contacts", "Messages", MessageSquare],
                ["visitors", "Visitors", Eye],
              ].map(([key, label, Icon]) => (
                <Panel key={key} className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><Icon size={22} /></div>
                  <h3 className="font-serif text-2xl text-espresso mt-5">{label}</h3>
                  <p className="text-sm text-slate-500 mt-1">Download CSV export from Firestore.</p>
                  <button onClick={() => exportRows(key)} className="btn-primary w-full mt-6"><Download size={15} /> Export CSV</button>
                </Panel>
              ))}
            </div>
          )}

          {!busy && tab === "controls" && (
            <div className="space-y-5">
              <Panel className="p-6 bg-espresso text-cream">
                <div className="flex items-center gap-3"><Shield size={22} className="text-gold" /><div><h2 className="font-serif text-3xl">Super Admin Controls</h2><p className="text-cream/70 text-sm">Only {SUPER_ADMIN_EMAIL} can change platform controls.</p></div></div>
              </Panel>
              <div className="grid md:grid-cols-2 gap-5">
                {[
                  ["registrationEnabled", "Registration", "Turn this off to stop new customer signups."],
                  ["paymentsEnabled", "Payments", "Turn this off to stop payment order creation."],
                  ["checkoutEnabled", "Checkout", "Pause checkout while keeping the storefront online."],
                  ["couponCheckoutEnabled", "Coupon Checkout", "Allow free or discounted coupon orders."],
                ].map(([key, label, desc]) => (
                  <Panel key={key} className="p-6 flex items-center justify-between gap-4">
                    <div><h3 className="font-serif text-2xl text-espresso">{label}</h3><p className="text-sm text-slate-500 mt-1">{desc}</p><Pill tone={controls[key] ? "good" : "bad"}>{controls[key] ? "Enabled" : "Disabled"}</Pill></div>
                    <button disabled={!isSuperAdmin} onClick={() => saveControls({ ...controls, [key]: !controls[key] })} className={`w-16 h-9 rounded-full p-1 transition-colors disabled:opacity-50 ${controls[key] ? "bg-emerald-500" : "bg-slate-300"}`}><span className={`block w-7 h-7 rounded-full bg-white transition-transform ${controls[key] ? "translate-x-7" : ""}`} /></button>
                  </Panel>
                ))}
              </div>
              <Panel className="p-6">
                <h3 className="font-serif text-2xl text-espresso mb-4">Admin Access</h3>
                <div className="divide-y divide-line">
                  {[SUPER_ADMIN_EMAIL, ADMIN_EMAIL].map((email) => (
                    <div key={email} className="py-4 flex items-center justify-between">
                      <div><div className="font-medium">{email}</div><div className="text-xs text-slate-500">{email === SUPER_ADMIN_EMAIL ? "Super admin: full controls" : "Admin: operations access"}</div></div>
                      <Pill tone="good">Active</Pill>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function OrderTable({ orders, updateOrder, compact = false }) {
  if (!orders.length) return <Empty>No orders found.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
          <tr><th className="px-5 py-4">Order</th><th className="px-5 py-4">Customer</th><th className="px-5 py-4">Items</th><th className="px-5 py-4">Payment</th><th className="px-5 py-4">Total</th><th className="px-5 py-4">Courier / Tracking</th><th className="px-5 py-4">Status</th></tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id} className="border-t border-line align-top" data-testid={`admin-order-${order.id}`}>
              <td className="px-5 py-4"><div className="font-semibold text-espresso">#{shortId(order.id)}</div><div className="text-xs text-slate-500">{ts(order.createdAt)}</div></td>
              <td className="px-5 py-4"><div>{order.shippingAddress?.firstName || "Guest"} {order.shippingAddress?.lastName || ""}</div><div className="text-xs text-slate-500">{order.email}</div>{!compact && <div className="text-xs text-slate-500">{order.shippingAddress?.city} {order.shippingAddress?.pincode}</div>}</td>
              <td className="px-5 py-4 min-w-[280px]">
                {(order.items || []).map((item, i) => (
                  <div key={i} className="text-xs mb-2">
                    <div className="font-medium text-slate-700">{item.name} x{item.qty || 1}</div>
                    <div className="text-slate-500">{[item.size, item.cover, item.personalization, item.plannerName ? `Name: ${item.plannerName}` : "", item.pageColor, item.color].filter(Boolean).join(" / ")}</div>
                  </div>
                ))}
              </td>
              <td className="px-5 py-4 uppercase text-xs">{order.paymentMethod || "N/A"}<div className="normal-case text-slate-500">{order.couponCode || ""}</div></td>
              <td className="px-5 py-4 font-medium">{formatINR(order.total || 0)}</td>
              <td className="px-5 py-4 min-w-[220px] space-y-2">
                <input defaultValue={order.trackingProvider || ""} onBlur={(e) => e.target.value !== (order.trackingProvider || "") && updateOrder(order.id, { trackingProvider: e.target.value.trim() })} placeholder="Provider: Delhivery, Amazon, Ekart..." className="w-full bg-white border border-line rounded-lg px-3 py-2 text-xs" />
                <input defaultValue={order.trackingNumber || ""} onBlur={(e) => e.target.value !== (order.trackingNumber || "") && updateOrder(order.id, { trackingNumber: e.target.value.trim() })} placeholder="Tracking ID / AWB" className="w-full bg-white border border-line rounded-lg px-3 py-2 text-xs" />
              </td>
              <td className="px-5 py-4 min-w-[180px]"><select value={order.status || "confirmed"} onChange={(e) => updateOrder(order.id, { status: e.target.value })} className="w-full bg-white border border-line rounded-lg px-3 py-2 text-xs capitalize">{ORDER_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}</select></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomerPanel({ customer, orders }) {
  if (!customer) return <Panel className="p-6 text-slate-500">Select a customer to see full details.</Panel>;
  const paidOrders = orders.filter(isPaidOrder);
  const latestOrder = orders[0] || {};
  const latestAddress = latestOrder.shippingAddress || {};
  const phone = customer.phone || latestAddress.phone || "";
  const displayName = customer.name || `${latestAddress.firstName || ""} ${latestAddress.lastName || ""}`.trim() || "Customer";
  return (
    <Panel className="p-6 xl:sticky xl:top-6 self-start">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-espresso text-cream flex items-center justify-center">{customer.email?.[0]?.toUpperCase() || "C"}</div>
        <div><h2 className="font-serif text-2xl text-espresso">{displayName}</h2><p className="text-sm text-slate-500">{customer.email || latestOrder.email}</p>{phone && <p className="text-sm text-slate-500">{phone}</p>}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-6">
        <div className="bg-ivory rounded-xl p-4"><div className="text-2xl font-semibold">{paidOrders.length}</div><div className="text-xs text-slate-500">Paid Orders</div></div>
        <div className="bg-ivory rounded-xl p-4"><div className="text-2xl font-semibold">{formatINR(paidOrders.reduce((s, o) => s + (o.total || 0), 0))}</div><div className="text-xs text-slate-500">Paid Spend</div></div>
      </div>
      <div className="mt-6 space-y-3 text-sm break-words">
        <div><span className="text-slate-500">Role:</span> {customer.isAdmin ? "Admin" : "Customer"}</div>
        <div><span className="text-slate-500">Digital access:</span> {customer.digitalAccess || "N/A"}</div>
        <div><span className="text-slate-500">Joined:</span> {ts(customer.createdAt)}</div>
        <div><span className="text-slate-500">Latest address:</span> {addressLine(latestAddress) || "N/A"}</div>
      </div>
      <div className="mt-6 border-t border-line pt-5">
        <h3 className="font-serif text-xl text-espresso mb-3">Purchase History</h3>
        {orders.length === 0 && <p className="text-sm text-slate-500">No purchases yet.</p>}
        {orders.map((order) => {
          const address = order.shippingAddress || {};
          return (
            <div key={order.id} className="py-4 border-b border-line text-sm">
              <div className="flex justify-between gap-3"><span className="font-medium text-espresso">#{shortId(order.id)}</span><span>{formatINR(order.total || 0)}</span></div>
              <div className="text-xs text-slate-500 mt-1">{statusLabel(order.status || "confirmed")} - {ts(order.createdAt)}</div>
              <div className="mt-3 rounded-lg bg-ivory p-3 space-y-2">
                <div><span className="text-slate-500">Phone:</span> {address.phone || "N/A"}</div>
                <div><span className="text-slate-500">Address:</span> {addressLine(address) || "N/A"}</div>
                <div><span className="text-slate-500">Payment:</span> {order.paymentMethod || "N/A"} / {order.paymentStatus || "N/A"}</div>
                {order.razorpayPaymentId && <div><span className="text-slate-500">Payment ID:</span> {order.razorpayPaymentId}</div>}
                {order.couponCode && <div><span className="text-slate-500">Coupon:</span> {order.couponCode}</div>}
                <div><span className="text-slate-500">Courier:</span> {order.trackingProvider || "Not added yet"}</div>
                <div><span className="text-slate-500">Tracking ID:</span> {order.trackingNumber || "Not added yet"}</div>
                <div className="space-y-2">
                  {(order.items || []).map((item, index) => (
                    <div key={index} className="border-t border-line pt-2 first:border-t-0 first:pt-0">
                      <div className="font-medium text-espresso">{item.name || "Planner"} x{item.qty || 1}</div>
                      <div className="text-xs text-slate-600 leading-relaxed">
                        {[item.size, item.cover, item.pageColor ? `Page: ${item.pageColor}` : "", item.color ? `Colour: ${item.color}` : "", item.personalization, item.plannerName ? `Name: ${item.plannerName}` : "No printed name"].filter(Boolean).join(" | ")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function AccessTable({ unlocks, orders, decideUnlock }) {
  return (
    <Panel className="overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500"><tr><th className="px-5 py-4">User</th><th className="px-5 py-4">Purchase</th><th className="px-5 py-4">Requested</th><th className="px-5 py-4">Status</th><th className="px-5 py-4 text-right">Action</th></tr></thead>
        <tbody>
          {unlocks.map((req) => {
            const email = (req.email || "").toLowerCase();
            const hasBought = orders.some((o) => o.userId === req.userId || (o.email || "").toLowerCase() === email || (o.emailLower || "").toLowerCase() === email);
            return (
              <tr key={req.id} className="border-t border-line">
                <td className="px-5 py-4"><div className="font-medium">{req.name || "Guest"}</div><div className="text-xs text-slate-500">{req.email}</div></td>
                <td className="px-5 py-4"><Pill tone={hasBought ? "good" : "neutral"}>{hasBought ? "Eligible" : "No order"}</Pill></td>
                <td className="px-5 py-4 text-slate-500">{ts(req.createdAt)}</td>
                <td className="px-5 py-4"><Pill tone={req.status === "approved" ? "good" : req.status === "denied" ? "bad" : "warn"}>{req.status || "pending"}</Pill></td>
                <td className="px-5 py-4 text-right">{(req.status || "pending") === "pending" ? <div className="flex gap-2 justify-end"><button onClick={() => decideUnlock(req, "approved")} className="btn-primary text-xs py-2 px-3"><Check size={13} /> Approve</button><button onClick={() => decideUnlock(req, "denied")} className="btn-outline text-xs py-2 px-3"><X size={13} /> Decline</button></div> : <span className="text-xs text-slate-500">Decided</span>}</td>
              </tr>
            );
          })}
          {unlocks.length === 0 && <tr><td colSpan={5}><Empty>No digital access requests.</Empty></td></tr>}
        </tbody>
      </table>
    </Panel>
  );
}

function SimpleTable({ title, rows, fields, timeField = "createdAt" }) {
  return (
    <Panel className="overflow-hidden">
      <div className="p-5 border-b border-line"><h2 className="font-serif text-2xl text-espresso">{title}</h2></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500"><tr>{fields.map((f) => <th key={f} className="px-5 py-4 capitalize">{f.replace(/([A-Z])/g, " $1")}</th>)}<th className="px-5 py-4">Time</th></tr></thead>
          <tbody>
            {rows.map((row) => <tr key={row.id} className="border-t border-line align-top">{fields.map((f) => <td key={f} className="px-5 py-4 max-w-md whitespace-pre-wrap">{row[f] || "N/A"}</td>)}<td className="px-5 py-4 text-slate-500">{ts(row[timeField])}</td></tr>)}
            {rows.length === 0 && <tr><td colSpan={fields.length + 1}><Empty>No data yet.</Empty></td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
